/**
 * @page /portal/zeiten
 * @description Meine Zeiten — Monatliche Arbeitszeiterfassung mit Soll/Ist-Vergleich
 *   und Saldo-Verlauf ueber die letzten 6 Monate.
 * @features
 *   - Monatsnavigation mit Soll/Ist-Stunden
 *   - Tages-Detail mit Check-in/Check-out Zeiten
 *   - Saldo-Verlauf als Flaechenchart (6 Monate)
 *   - KPI-Cards (Soll, Ist, Saldo, Arbeitstage)
 * @api GET /api/portal/meine-zeiten?monat=YYYY-MM
 * @api GET /api/portal/saldo-verlauf?monate=6
 * @roles mitarbeiter, admin, manager
 * @i18n portal.times
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import MitarbeiterAvatar from '../components/MitarbeiterAvatar';
import { ClipboardList, Timer, BarChart3, Clock } from 'lucide-react';
import { Card, KpiCard, PageHeader } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';
import PortalZeitkontoPage from './PortalZeitkontoPage';

const API = import.meta.env.VITE_API_URL || '';

function fmtHours(h) {
  if (h == null) return '–';
  return h.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'h';
}

// #1776: Zeiten im Monatsnachweis immer als h:mm (nie dezimal).
function fmtHm(min) {
  if (min == null) return '–';
  const a = Math.abs(Math.round(min));
  return `${min < 0 ? '-' : ''}${Math.floor(a / 60)}:${String(a % 60).padStart(2, '0')}`;
}

function fmtTime(dtStr) {
  if (!dtStr) return '–';
  return dtStr.slice(11, 16);
}

function StatCard({ label, value, sub, color, icon: Icon, iconColor }) {
  return (
    <div className="bg-surface-card border border-line rounded-card p-4">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className={`w-4 h-4 ${iconColor || 'text-[var(--text-secondary)]'}`} />}
        <span className="text-[11px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-xl font-bold font-mono text-content">{value}</div>
      {sub && <div className="text-xs text-[var(--text-tertiary)] mt-1">{sub}</div>}
    </div>
  );
}

export default function MaPortalZeiten() {
  const { t } = useLanguage();
  const [tab, setTab] = useState('monat');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [verlauf, setVerlauf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const monatStr = useMemo(() => `${year}-${String(month).padStart(2, '0')}`, [year, month]);

  const prevMonth = useCallback(() => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  }, [month]);

  const nextMonth = useCallback(() => {
    const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
    if (isCurrentMonth) return;
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  }, [month, year, now]);

  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

  // #1776: Summenzeile aus den SSoT-Tagen. Delta bleibt der IST-basierte Monatssaldo,
  //   damit Portal, Desktop und PDF exakt dieselbe Zahl zeigen (Netto + ZA = IST).
  const summen = useMemo(() => {
    const s = { soll: 0, ist: 0, arbeit: 0, za: 0, delta: 0 };
    for (const tag of (data?.tage || [])) {
      s.soll += Number(tag.soll_min || 0);
      s.ist += Number(tag.ist_min || 0);
      s.za += Number(tag.za_min || 0);
      s.arbeit += Number(tag.arbeit_min != null ? tag.arbeit_min : (tag.ist_min || 0));
    }
    // #1864 F5: Delta = Σ persistierte Tages-Deltas bis gestern (Server `anzeige`), nie `ist − soll`.
    s.delta = data?.anzeige?.delta_min != null ? data.anzeige.delta_min : (data?.saldo != null ? Math.round(data.saldo * 60) : (s.ist - s.soll));
    return s;
  }, [data]);

  // Load month data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/portal/meine-zeiten?monat=${monatStr}`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || t('common.loadingError'));
      }
      setData(await res.json());
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [monatStr]);

  // Load saldo verlauf
  const loadVerlauf = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/portal/saldo-verlauf?monate=6`, { credentials: 'include' });
      if (res.ok) setVerlauf(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadVerlauf(); }, [loadVerlauf]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-inset rounded w-1/3" />
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map(i => <div key={i} className="h-24 bg-surface-inset rounded-card" />)}
          </div>
          <div className="h-64 bg-surface-inset rounded-card" />
        </div>
      </div>
    );
  }

  const monthName = t(`common.months.${month - 1}`);
  const monthShort = idx => t(`common.months_short.${idx}`);
  const monthLong = idx => t(`common.months.${idx}`);

  return (
    <div className="p-8">
      <PageHeader title={t('portalTimes.title')} subtitle={`${monthName} ${year}`} className="mb-section" />

      {/* Tab Bar */}
      <div className="flex gap-1 mb-section bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] w-fit">
        <button onClick={() => setTab('monat')} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'monat' ? 'bg-white shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
          <ClipboardList className="w-3.5 h-3.5" /> {t('portalTimes.tabMonth')}
        </button>
        <button onClick={() => setTab('zeitkonto')} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'zeitkonto' ? 'bg-white shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
          <Clock className="w-3.5 h-3.5" /> {t('portalTimes.tabTimeAccount')}
        </button>
      </div>

      {tab === 'zeitkonto' ? (
        <PortalZeitkontoPage embedded />
      ) : (<>

      {/* Month Selector */}
      <div className="flex items-center gap-3 mb-section">
        <button
          onClick={prevMonth}
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-surface-inset border border-line-hover text-content-tertiary hover:text-content hover:bg-surface-active transition-colors"
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-sm font-semibold text-content min-w-[140px] text-center">
          {monthName} {year}
        </span>
        <button
          onClick={nextMonth}
          disabled={isCurrentMonth}
          className={`w-8 h-8 flex items-center justify-center rounded-lg bg-surface-inset border border-line-hover transition-colors ${
            isCurrentMonth ? 'text-[var(--text-tertiary)] cursor-default' : 'text-content-tertiary hover:text-content hover:bg-surface-active'
          }`}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-card p-4 text-sm text-red-400 mb-section">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-3 gap-3 mb-section">
            <StatCard
              icon={ClipboardList}
              iconColor="text-[#338edc]"
              label={t('portalTimes.target')}
              value={fmtHours(data.anzeige ? data.anzeige.soll_min / 60 : data.soll_gesamt)}
              sub={data.anzeige?.stichtag ? t('portal.zeitkonto.standBis', { defaultValue: 'Stand bis {{datum}}', datum: `${data.anzeige.stichtag.slice(8, 10)}.${data.anzeige.stichtag.slice(5, 7)}.${data.anzeige.stichtag.slice(0, 4)}` }) : t('portalTimes.plannedHours')}
              color="#94a3b8"
            />
            <StatCard
              icon={Timer}
              iconColor="text-[#F59E0B]"
              label={t('portalTimes.actual')}
              value={fmtHours(data.anzeige ? data.anzeige.ist_min / 60 : data.ist_gesamt)}
              sub={t('portalTimes.workedHours')}
              color="#338edc"
            />
            <StatCard
              icon={BarChart3}
              iconColor="text-[#10B981]"
              label={t('portalTimes.balance')}
              value={(() => { const h = data.anzeige?.delta_min != null ? data.anzeige.delta_min / 60 : data.saldo; return `${h >= 0 ? '+' : ''}${fmtHours(h)}`; })()}
              sub={(data.anzeige?.delta_min != null ? data.anzeige.delta_min : data.saldo) >= 0 ? t('portalTimes.overtime') : t('portalTimes.deficit')}
              color={(data.anzeige?.delta_min != null ? data.anzeige.delta_min : data.saldo) >= 0 ? '#22c55e' : '#ef4444'}
            />
          </div>

          {/* #1776 Kontoauszug ZA-Konto — dieselben Zahlen wie die SSoT, nur benannt. */}
          {data.kontoauszug && (
            <div className="bg-surface-card border border-line rounded-card p-5 mb-section">
              <h3 className="text-sm font-bold text-content uppercase tracking-wider mb-3">
                {t('portal.zeitkonto.kontoauszug', 'Zeitausgleichs-Konto')}
              </h3>
              <div className="space-y-1.5 text-sm">
                {[
                  [t('portal.zeitkonto.saldoBeginn', 'Saldo Monatsbeginn'), data.kontoauszug.za_saldo_beginn],
                  [`+ ${t('portal.zeitkonto.veraenderungArbeitszeit', 'Veränderung durch Arbeitszeit')}`, data.kontoauszug.veraenderung_arbeitszeit],
                  [`− ${t('portal.zeitkonto.abzugZeitausgleich', 'Abzug durch Zeitausgleich')}`, -Math.abs(Number(data.kontoauszug.za_abzug_summe) || 0)],
                ].map(([label, val]) => (
                  <div key={label} className="flex justify-between">
                    <span className="text-content-secondary">{label}</span>
                    <span className="font-mono text-content">{fmtHm(Math.round((Number(val) || 0) * 60))}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-line pt-1.5 font-bold">
                  <span className="text-content">= {t('portal.zeitkonto.saldoEnde', 'Saldo Monatsende')}</span>
                  <span className="font-mono text-content">{fmtHm(Math.round((Number(data.kontoauszug.za_saldo_ende) || 0) * 60))}</span>
                </div>
              </div>
              <div className="text-[11px] text-[var(--text-tertiary)] mt-3">
                {t('portal.zeitkonto.saldoHinweis', 'Der Saldo Monatsende ist der Saldo Monatsbeginn des Folgemonats.')}
              </div>
            </div>
          )}

          {/* Day Table */}
          <div className="bg-surface-card border border-line rounded-card overflow-hidden mb-section">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">
                    <th className="py-3 px-3 text-left">{t('portalTimes.date')}</th>
                    <th className="py-3 px-2 text-left w-10">{t('portalTimes.weekday')}</th>
                    <th className="py-3 px-2 text-center">{t('portalTimes.clockIn')}</th>
                    <th className="py-3 px-2 text-center">{t('portalTimes.clockOut')}</th>
                    <th className="py-3 px-2 text-right">{t('portal.zeitkonto.netto', 'Netto')}</th>
                    <th className="py-3 px-2 text-right">{t('portalTimes.target')}</th>
                    <th className="py-3 px-3 text-right">+/&ndash;</th>
                    <th className="py-3 px-2 text-right">ZA</th>
                    <th className="py-3 px-3 text-right">{t('portal.zeitkonto.zaKontoAbzug', 'ZA-Konto Abzug')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tage.map(tag => {
                    const isWeekend = tag.wochentag === 'Sa' || tag.wochentag === 'So';
                    const isFeiertag = !!tag.feiertag;
                    const hasEntries = tag.eintraege.length > 0;
                    const isMissing = !isWeekend && !isFeiertag && tag.soll > 0 && tag.ist === 0;
                    const today = new Date();
                    const isToday = tag.datum === `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                    const isFuture = tag.datum > `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

                    const firstIn = hasEntries ? fmtTime(tag.eintraege[0].check_in) : '–';
                    const lastOut = hasEntries ? fmtTime(tag.eintraege[tag.eintraege.length - 1].check_out) : '–';

                    // #1776: Netto = echte Arbeitszeit, ZA separat ausgewiesen (SSoT-Werte).
                    const zaMin = Number(tag.za_min || 0);
                    const sollMin = Number(tag.soll_min != null ? tag.soll_min : Math.round((tag.soll || 0) * 60));
                    const nettoMin = Number(tag.arbeit_min != null ? tag.arbeit_min : Math.round((tag.ist || 0) * 60));
                    const deltaMin = Number(tag.delta_min != null ? tag.delta_min : (nettoMin - sollMin));

                    return (
                      <tr
                        key={tag.datum}
                        className={`border-b border-line/50 transition-colors ${
                          isToday ? 'bg-primary-500/[0.05]'
                          : isFeiertag ? 'bg-purple-500/[0.03]'
                          : isWeekend ? 'bg-surface-inset/50'
                          : isMissing && !isFuture ? 'bg-red-500/[0.03]'
                          : ''
                        }`}
                      >
                        <td className="py-2.5 px-3 font-mono text-xs text-content-secondary">
                          {tag.datum.slice(8, 10)}.{tag.datum.slice(5, 7)}.
                          {isToday && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-primary-500/20 text-primary-400 font-semibold">{t('portalTimes.today')}</span>}
                        </td>
                        <td className={`py-2.5 px-2 text-xs font-medium ${isWeekend ? 'text-[var(--text-tertiary)]' : 'text-content-secondary'}`}>
                          {tag.wochentag}
                        </td>
                        <td className="py-2.5 px-2 text-center font-mono text-xs text-content-secondary">
                          {isFuture ? '' : firstIn}
                          {/* #1777 Punkt 1: geklemmter Tag — echten Check-in mit ausweisen. */}
                          {!isFuture && tag.arbeitszeit_ab && tag.check_in && (
                            <div className="text-[10px] text-[var(--text-tertiary)] leading-tight"
                                 title={t('portal.zeitkonto.checkInTooltip', 'Tatsächliche Stempelzeit — die Arbeitszeit wird erst ab dem frühesten Beginn gewertet.')}>
                              {t('portal.zeitkonto.checkIn', 'Check-in')} {fmtTime(tag.check_in)}
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 px-2 text-center font-mono text-xs text-content-secondary">
                          {isFuture ? '' : lastOut}
                        </td>
                        <td className="py-2.5 px-2 text-right font-mono font-bold text-xs text-content">
                          {isFuture ? '' : nettoMin > 0 ? fmtHm(nettoMin) : (isWeekend || isFeiertag) ? '' : '–'}
                        </td>
                        <td className="py-2.5 px-2 text-right font-mono text-[var(--text-secondary)] text-xs">
                          {isFeiertag ? (
                            <span className="text-purple-400 text-[10px]">{tag.feiertag}</span>
                          ) : sollMin > 0 ? fmtHm(sollMin) : ''}
                        </td>
                        <td className={`py-2.5 px-3 text-right font-mono font-bold text-xs ${
                          isFuture || (sollMin === 0 && nettoMin === 0 && zaMin === 0) ? 'text-[var(--text-tertiary)]' : 'text-content'
                        }`}>
                          {isFuture ? '' : (sollMin === 0 && nettoMin === 0 && zaMin === 0) ? '' : `${deltaMin >= 0 ? '+' : ''}${fmtHm(deltaMin)}`}
                        </td>
                        <td className="py-2.5 px-2 text-right font-mono text-xs text-content-secondary">
                          {!isFuture && zaMin > 0 ? fmtHm(zaMin) : '—'}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-xs text-content-secondary">
                          {!isFuture && zaMin > 0 && tag.za_abzug_min > 0 ? fmtHm(-tag.za_abzug_min) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-line-hover bg-surface-card">
                    <td colSpan={4} className="py-3 px-3 text-xs font-bold text-content uppercase">{t('portalTimes.total')}</td>
                    <td className="py-3 px-2 text-right font-mono font-bold text-xs text-content">{fmtHm(summen.arbeit)}</td>
                    <td className="py-3 px-2 text-right font-mono text-[var(--text-secondary)] text-xs">{fmtHm(summen.soll)}</td>
                    <td className="py-3 px-3 text-right font-mono font-bold text-xs text-content">
                      {summen.delta >= 0 ? '+' : ''}{fmtHm(summen.delta)}
                    </td>
                    <td className="py-3 px-2 text-right font-mono font-bold text-xs text-content">
                      {summen.za > 0 ? fmtHm(summen.za) : '—'}
                    </td>
                    <td className="py-3 px-3" />
                  </tr>
                  {summen.za > 0 && (
                    <tr>
                      <td colSpan={9} className="pb-3 px-3 text-[10px] text-[var(--text-tertiary)]">
                        {t('portal.zeitkonto.netto', 'Netto')} {fmtHm(summen.arbeit)} + {t('portal.zeitkonto.zeitausgleich', 'Zeitausgleich')} {fmtHm(summen.za)} = IST {fmtHm(summen.ist)}
                      </td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          </div>

          {/* Saldo Trend Chart */}
          {verlauf?.verlauf?.length > 0 && (
            <div className="bg-surface-card border border-line rounded-card p-5">
              <h3 className="text-sm font-bold text-content uppercase tracking-wider mb-4">{t('portalTimes.balanceTrend')}</h3>
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={verlauf.verlauf} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
                    <defs>
                      <linearGradient id="saldoGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#338edc" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#338edc" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default,#E5E7EB)" strokeOpacity={0.5} />
                    <XAxis
                      dataKey="monat"
                      tick={{ fill: 'var(--text-tertiary,#6B7280)', fontSize: 11 }}
                      tickFormatter={v => {
                        const [, m] = v.split('-');
                        return monthShort(parseInt(m) - 1);
                      }}
                    />
                    <YAxis tick={{ fill: 'var(--text-tertiary,#6B7280)', fontSize: 11 }} tickFormatter={v => `${v}h`} />
                    <Tooltip
                      contentStyle={{ background: 'var(--bg-card,#ffffff)', border: '1px solid var(--border-default,#E5E7EB)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary,#1A1A2E)' }}
                      labelStyle={{ color: 'var(--text-secondary,#4B5563)' }}
                      labelFormatter={v => {
                        const [y, m] = v.split('-');
                        return `${monthLong(parseInt(m) - 1)} ${y}`;
                      }}
                      formatter={(val, name) => [`${val >= 0 ? '+' : ''}${val.toFixed(1)}h`, name === 'kumuliert' ? t('portalTimes.cumulative') : t('portalTimes.monthlyBalance')]}
                    />
                    <ReferenceLine y={0} stroke="var(--border-default,#E5E7EB)" />
                    <Area type="monotone" dataKey="kumuliert" stroke="#338edc" fill="url(#saldoGrad)" strokeWidth={2} dot={{ r: 3, fill: '#338edc' }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              {/* Legend */}
              <div className="flex items-center gap-6 mt-3 text-[10px] text-[var(--text-tertiary)]">
                {verlauf.verlauf.map(v => (
                  <span key={v.monat} className="flex items-center gap-1">
                    <span className="font-mono font-bold text-content">
                      {v.diff >= 0 ? '+' : ''}{v.diff.toFixed(1)}h
                    </span>
                    {monthShort(parseInt(v.monat.split('-')[1]) - 1)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      </>)}
    </div>
  );
}
